import { Bar, Box, Cyl, RBox, Slab, Sphere } from '../../render/parts'
import type { Tone } from '../../render/theme'

/**
 * Reusable seating sub-assemblies.
 *
 * Everything here is authored to the catalog contract: origin at the centre of
 * the footprint, y = 0 on the floor, front facing +Z.
 */

export interface StarBaseProps {
  tone?: Tone
  arms?: number
  radius?: number
  /** Height of the top of the hub, where the gas cylinder starts. */
  hubTop?: number
}

/** Five-star castor base with a hub, as found on every task chair. */
export function StarBase({ tone = 'darkDeep', arms = 5, radius = 0.31, hubTop = 0.1 }: StarBaseProps) {
  const armLength = radius - 0.07
  return (
    <group>
      <Cyl tone={tone} r={0.055} rTop={0.048} h={hubTop - 0.03} position={[0, hubTop - (hubTop - 0.03) / 2, 0]} segments={20} />
      {Array.from({ length: arms }, (_, index) => {
        const angle = (index / arms) * Math.PI * 2
        return (
          <group key={index} rotation={[0, angle, 0]}>
            <Slab
              tone={tone}
              size={[0.058, armLength, 0.026]}
              radius={0.022}
              position={[0, 0.062, armLength / 2 + 0.035]}
            />
            <Cyl tone={tone} r={0.012} h={0.045} position={[0, 0.056, radius - 0.035]} segments={10} />
            <Cyl
              tone={tone}
              axis="x"
              r={0.027}
              h={0.021}
              segments={16}
              position={[0, 0.027, radius - 0.035]}
            />
          </group>
        )
      })}
    </group>
  )
}

export interface GasLiftProps {
  tone?: Tone
  from?: number
  to?: number
}

/** Telescopic column between the base hub and the seat. */
export function GasLift({ tone = 'darkDeep', from = 0.1, to = 0.42 }: GasLiftProps) {
  const sleeveHeight = (to - from) * 0.55
  return (
    <group>
      <Cyl tone={tone} r={0.048} h={sleeveHeight} position={[0, from + sleeveHeight / 2, 0]} segments={18} />
      <Cyl
        tone={tone}
        r={0.03}
        h={to - from - sleeveHeight + 0.02}
        position={[0, from + sleeveHeight + (to - from - sleeveHeight) / 2, 0]}
        segments={16}
      />
    </group>
  )
}

export interface ArmrestProps {
  tone?: Tone
  seatHeight?: number
  width?: number
}

/** Pair of L-shaped armrests straddling the seat. */
export function Armrests({ tone = 'darkDeep', seatHeight = 0.46, width = 0.5 }: ArmrestProps) {
  const x = width / 2 + 0.015
  return (
    <group>
      {[-1, 1].map((side) => (
        <group key={side} position={[side * x, 0, 0]}>
          {/* Post rises from under the seat pan; the pad overlaps its top by
              1 cm so the two never read as two stacked blocks. */}
          <Box tone={tone} size={[0.026, 0.22, 0.045]} position={[0, seatHeight + 0.07, -0.085]} />
          <Slab tone={tone} size={[0.05, 0.23, 0.026]} radius={0.02} position={[0, seatHeight + 0.175, -0.015]} />
        </group>
      ))}
    </group>
  )
}

export interface TaskChairProps {
  tone?: Tone
  /** Seat top height in metres. */
  seatHeight?: number
  armrests?: boolean
  headrest?: boolean
  /** Colour of the backrest mesh — the one part offices actually customise. */
  backTone?: Tone
  backColor?: string
}

/**
 * The swivel task chair: star base, gas lift, contoured seat, tall mesh back.
 * Footprint 0.62 x 0.62, height ~1.05 (1.22 with headrest).
 */
export function TaskChair({
  tone = 'darkDeep',
  seatHeight = 0.46,
  armrests = true,
  headrest = false,
  backTone = 'dark',
  backColor,
}: TaskChairProps) {
  const seatThickness = 0.075
  const seatCentre = seatHeight - seatThickness / 2
  const backBottom = seatHeight + 0.015
  const backHeight = 0.56
  const backZ = -0.195
  /**
   * Rx maps a point above the pivot to z = y·sin(θ), so a POSITIVE angle tips
   * the top of the backrest towards the sitter. Reclining needs a negative one,
   * and 7° is what a task chair actually sits at — anything more reads as a
   * lounger.
   */
  const backTilt = -0.12

  return (
    <group>
      <StarBase tone={tone} />
      <GasLift tone={tone} from={0.1} to={seatCentre} />

      {/* seat pan, tipped a couple of degrees back */}
      <group position={[0, seatCentre, -0.01]} rotation={[-0.045, 0, 0]}>
        <Slab tone={tone} size={[0.5, 0.47, seatThickness]} radius={0.07} />
      </group>

      {/* Lumbar bridge: overlaps both the seat and the backrest foot so the two
          shells never read as separate floating slabs. */}
      <Box tone={tone} size={[0.09, 0.12, 0.1]} position={[0, seatHeight + 0.02, -0.175]} />

      {/* Backrest hinged at its BOTTOM edge, so changing the recline never
          opens a gap above the seat. */}
      <group position={[0, backBottom, backZ]} rotation={[backTilt, 0, 0]}>
        <group position={[0, backHeight / 2, 0]}>
          <Slab
            tone={backTone}
            color={backColor}
            size={[0.46, backHeight, 0.05]}
            radius={0.07}
            rotation={[Math.PI / 2, 0, 0]}
          />
          {headrest ? (
            <Slab
              tone={tone}
              size={[0.3, 0.13, 0.05]}
              radius={0.05}
              position={[0, backHeight / 2 + 0.1, 0]}
              rotation={[Math.PI / 2, 0, 0]}
            />
          ) : null}
        </group>
      </group>

      {armrests ? <Armrests tone={tone} seatHeight={seatHeight} width={0.5} /> : null}
    </group>
  )
}

export interface CantileverChairProps {
  tone?: Tone
  frameTone?: Tone
  seatHeight?: number
}

/**
 * Sled-frame meeting chair — the cantilever "C" profile seen around boardroom
 * tables. Footprint 0.52 x 0.56, height 0.86.
 */
/**
 * Sled-frame meeting chair.
 *
 * The frame is one continuous tube per side, read as a flat "C": floor runner →
 * front upright → seat rail → back upright, with the two sides tied by braces at
 * the front and back of the sled. The back upright stops at the backrest's
 * mid-height and tucks just behind it, so it reads as attached rather than as a
 * pole sticking out of the top.
 */
export function CantileverChair({
  tone = 'dark',
  frameTone = 'metal',
  seatHeight = 0.45,
}: CantileverChairProps) {
  const x = 0.2
  const r = 0.014
  const front = 0.22
  const back = -0.22
  const runnerY = 0.016
  const railY = seatHeight - 0.055

  const backTilt = 0.14
  const backHeight = 0.38
  const backThickness = 0.045
  const backCentreY = seatHeight + 0.05 + backHeight / 2
  const backCentreZ = -0.2
  /** Flush against the back face at mid-height, so the tube tucks into the shell. */
  const upperZ = backCentreZ - backThickness / 2 - r * 0.4

  return (
    <group>
      {[-1, 1].map((side) => (
        <group key={side}>
          <Bar tone={frameTone} r={r} from={[side * x, runnerY, back]} to={[side * x, runnerY, front]} />
          <Bar tone={frameTone} r={r} from={[side * x, runnerY, front]} to={[side * x, railY, front - 0.015]} />
          <Bar tone={frameTone} r={r} from={[side * x, railY, front - 0.015]} to={[side * x, railY, back + 0.045]} />
          <Bar tone={frameTone} r={r} from={[side * x, railY, back + 0.045]} to={[side * x, backCentreY, upperZ]} />
          {[
            [runnerY, front] as const,
            [railY, front - 0.015] as const,
            [railY, back + 0.045] as const,
          ].map(([jy, jz]) => (
            <Sphere key={`${jy}-${jz}`} tone={frameTone} r={r} position={[side * x, jy, jz]} />
          ))}
        </group>
      ))}
      {/* Sled braces, front and back, so the frame reads as a closed loop. */}
      <Bar tone={frameTone} r={r} from={[-x, runnerY, back]} to={[x, runnerY, back]} />
      <Bar tone={frameTone} r={r} from={[-x, runnerY, front]} to={[x, runnerY, front]} />

      <Slab tone={tone} size={[0.46, 0.45, 0.05]} radius={0.06} position={[0, seatHeight - 0.025, 0.005]} />
      {/* Negative tilt: the top of a backrest leans AWAY from the sitter. */}
      <group position={[0, backCentreY, backCentreZ]} rotation={[-backTilt, 0, 0]}>
        <Slab
          tone={tone}
          size={[0.44, backHeight, backThickness]}
          radius={0.06}
          rotation={[Math.PI / 2, 0, 0]}
        />
      </group>
    </group>
  )
}

export interface StoolProps {
  tone?: Tone
  frameTone?: Tone
  height?: number
}

/** Round bar stool with a footring — breakout areas and high tables. */
export function Stool({ tone = 'dark', frameTone = 'metal', height = 0.66 }: StoolProps) {
  return (
    <group>
      <Cyl tone={frameTone} r={0.19} h={0.02} position={[0, 0.01, 0]} segments={26} />
      <Cyl tone={frameTone} r={0.035} h={height - 0.07} position={[0, (height - 0.07) / 2 + 0.02, 0]} segments={18} />
      <Cyl tone={frameTone} r={0.15} rTop={0.15} h={0.014} position={[0, 0.2, 0]} segments={26} openEnded />
      <Slab tone={tone} size={[0.36, 0.36, 0.05]} radius={0.17} position={[0, height - 0.025, 0]} />
    </group>
  )
}

export interface LoungeChairProps {
  tone?: Tone
  accent?: string
  legTone?: Tone
}

/** Soft lounge armchair for breakout corners. Footprint 0.78 x 0.76. */
export function LoungeChair({ tone = 'shellSoft', accent, legTone = 'wood' }: LoungeChairProps) {
  const seatHeight = 0.4
  return (
    <group>
      {(
        [
          [-0.28, -0.26],
          [0.28, -0.26],
          [-0.28, 0.26],
          [0.28, 0.26],
        ] as const
      ).map(([x, z]) => (
        <Bar key={`${x},${z}`} tone={legTone} r={0.018} from={[x, 0, z]} to={[x * 0.86, seatHeight - 0.1, z * 0.86]} />
      ))}
      <RBox tone={tone} color={accent} size={[0.72, 0.14, 0.68]} radius={0.03} position={[0, seatHeight - 0.05, 0]} />
      <RBox tone={tone} color={accent} size={[0.72, 0.44, 0.14]} radius={0.03} position={[0, seatHeight + 0.2, -0.28]} rotation={[-0.1, 0, 0]} />
      {[-1, 1].map((side) => (
        <RBox
          key={side}
          tone={tone}
          color={accent}
          size={[0.12, 0.2, 0.62]}
          radius={0.03}
          position={[side * 0.3, seatHeight + 0.08, 0.01]}
        />
      ))}
    </group>
  )
}

export interface SofaProps {
  tone?: Tone
  accent?: string
  seats?: number
  width?: number
}

/** Two/three-seat sofa built from the same cushion vocabulary as the armchair. */
export function Sofa({ tone = 'shellSoft', accent, seats = 2, width = 1.8 }: SofaProps) {
  const seatHeight = 0.42
  const cushion = (width - 0.28) / seats
  return (
    <group>
      {[-1, 1].map((side) => (
        <group key={side}>
          <Bar tone="metalDark" r={0.018} from={[side * (width / 2 - 0.1), 0, -0.28]} to={[side * (width / 2 - 0.12), seatHeight - 0.12, -0.26]} />
          <Bar tone="metalDark" r={0.018} from={[side * (width / 2 - 0.1), 0, 0.28]} to={[side * (width / 2 - 0.12), seatHeight - 0.12, 0.26]} />
        </group>
      ))}
      <RBox tone={tone} size={[width, 0.16, 0.8]} radius={0.03} position={[0, seatHeight - 0.06, 0]} />
      {Array.from({ length: seats }, (_, index) => (
        <RBox
          key={index}
          tone={tone}
          color={accent}
          size={[cushion - 0.03, 0.11, 0.66]}
          radius={0.035}
          position={[(index - (seats - 1) / 2) * cushion, seatHeight + 0.06, 0.05]}
        />
      ))}
      <RBox tone={tone} size={[width, 0.42, 0.16]} radius={0.035} position={[0, seatHeight + 0.2, -0.32]} rotation={[-0.08, 0, 0]} />
      {[-1, 1].map((side) => (
        <RBox key={side} tone={tone} size={[0.14, 0.24, 0.78]} radius={0.035} position={[side * (width / 2 - 0.07), seatHeight + 0.1, 0]} />
      ))}
    </group>
  )
}
